import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import {
  Icon,
  Table,
  SemanticICONS,
  SemanticCOLORS,
  Popup,
  Button,
  Transition,
  Segment,
  Header,
  Dimmer,
  Loader,
  Placeholder
} from 'semantic-ui-react';
import { StyledTableRow } from '~/styles/table';
import { Link } from 'react-router-dom';
import CustomIcon from '../CustomIcon';
import { IconSizeProp } from 'semantic-ui-react/dist/commonjs/elements/Icon/Icon';
import { StyledActionLoader, StyledLoaderWrapper, StyledNoResultsMessage, StyledTableWrapper } from './style'
import { ThemeContext } from 'styled-components';
import cloneDeep from 'lodash.clonedeep'
import InfoPopup from '../InfoPopup';
import { useAuth } from '~/routes/components/AuthProvider';
import TableFooter from './TableFooter';
import { cellRendererMapping } from './utils';

type ActionColumnType = {
  popupMessage: string | React.ReactNode
  type: string,
  name: string,
  size?: string,
  link?: string,
  display?: any[],
  color?: string,
  actionType?: string,
  verifiedAction?: number | string,
  verifiedActionKey?: string,
  displayedText?: string,
  displayedTextKey?: string
  renderActions?: (action: ActionColumnType, actionType: string) => JSX.Element
  replaceKey?: string
}
interface TableProps {
  tableColumnHeaders: any[],
  dataFiltered?: any,
  pagination?: boolean,
  handleChangeDataFiltered?: (data: any, tableColumnHeaders?) => void,
  actions?: ActionColumnType[],
  rows: any[],
  total: number,
  handleClickAction?: (row, type, index?) => void,
  loading?: boolean,
  search?: boolean,
  options?: any,
  handleCellValueChange?: (value, index?, row?) => void,
  removePagination?: boolean,
  customTableFooter?: React.ReactNode
  animated?: boolean
  idKey?: string
  isFetchingData?: boolean,
  renderColumn?: (type: string, value: any, row?, index?) => JSX.Element
  upward?: boolean
  infoNextToText?: boolean
  loadingAction?: {
    type: string
    id: number | string
    value: boolean
    idKey: string
  }
  disableRowId?: string
}

const TableComponent: React.FC<TableProps> = (props) => {
  const { t } = useTranslation()
  const { tableColumnHeaders, handleChangeDataFiltered } = props
  const [dataFiltered, setDataFiltered] = useState(props.dataFiltered)
  const { firstColor } = useContext(ThemeContext)
  const { total, rows } = props
  const { systemData: { amountUnit } } = useAuth()

  useEffect(() => {
    setDataFiltered(props.dataFiltered)
  }, [props.dataFiltered])

  const snakeToCamel = str =>
    str.toLowerCase().replace(/([-_][a-z])/g, group =>
      group
        .toUpperCase()
        .replace('-', '')
        .replace('_', '')
    );

  useEffect(() => {
    if (dataFiltered && JSON.stringify(props.dataFiltered) !== JSON.stringify(dataFiltered)) {
      const tempColumnHeader = cloneDeep(tableColumnHeaders)
      const { column, sort } = dataFiltered
      if (column && sort) {
        const columnKeyRevertedToCamel = snakeToCamel(column)
        const indexColumn = tableColumnHeaders.findIndex(tableColumn => tableColumn.key === columnKeyRevertedToCamel)
        if (indexColumn >= 0) {
          tempColumnHeader[indexColumn].sorted = sort
          tempColumnHeader.forEach((tableColumn, index) => {
            if (tableColumn.sorted && index !== indexColumn) {
              tableColumn.sorted = 'desc'
            }
          })
        }
      }
      handleChangeDataFiltered(dataFiltered, tempColumnHeader)
    }
  }, [dataFiltered])

  function renderLinkAction(row, action: ActionColumnType) {
    const verified = action.verifiedAction !== undefined ? row[action.verifiedActionKey] === action.verifiedAction : true
    const show = action.display ? action.display.find(actionStatus => actionStatus === row.status) !== undefined : true
    return show && verified && <Popup
      key={row.id + action.popupMessage}
      content={action.popupMessage}
      trigger={<Link key={row.id + 'link'} to={action.link.replace(action.replaceKey || ':id', row.id)}>
        <CustomIcon
          name={action.name as SemanticICONS}
          size={action.size as IconSizeProp}
        />
      </Link>}
    />
  }

  function renderLoader() {
    return <StyledLoaderWrapper key={Math.random()}>
      <StyledActionLoader active />
    </StyledLoaderWrapper>
  }

  function renderIconAction(row, action: ActionColumnType, index: number) {
    const show = action.display ? action.display.find(actionStatus => actionStatus === row.status) !== undefined : true
    const verified = action.verifiedAction !== undefined ? row[action.verifiedActionKey] === action.verifiedAction : true
    const displayLoader = props?.loadingAction?.value && props?.loadingAction?.id === row[props?.loadingAction?.idKey] &&
      action.actionType === props?.loadingAction?.type
    return displayLoader ? renderLoader() : show && verified ? <Popup
      key={`${row.id + action.actionType}-icon-action`}
      content={action.popupMessage}
      trigger={<CustomIcon
        name={action.name as SemanticICONS}
        size={action.size as IconSizeProp}
        color={action.color as SemanticCOLORS}
        onClick={() => props.handleClickAction(row, action.actionType, index)}
      />}
    /> : row[action.displayedTextKey] && index === 0 ? action.displayedText : ''
  }

  function actionColumn(row) {
    return <Button.Group key={row.id + 'button'} style={{ alignItems: 'end' }}>
      {props.actions.map((action, index) => {
        if (action.renderActions) {
          const displayLoader = props?.loadingAction?.value && props?.loadingAction?.id === row[props?.loadingAction?.idKey] &&
            action.actionType === props?.loadingAction?.type
          return displayLoader ? renderLoader() : <div key={`${row.id + action.actionType}-loading-action`}>{action.renderActions(row, action.actionType)}</div>
        }
        return action.type === 'link' ? renderLinkAction(row, action) : renderIconAction(row, action, index)
      })}
    </Button.Group>
  }

  function renderActionColumn(row) {
    return props.actions?.length > 0 ? actionColumn(row) : props.actions?.length === 0 && t('No actions')
  }

  function renderCellBasedOnType(value, row, index, column) {
    const { type } = column;
    const cellRenderer = cellRendererMapping[type] || cellRendererMapping.default;
    return cellRenderer({
      value,
      row,
      column,
      options: props.options,
      amountUnit,
      firstColor,
      renderColumn: props.renderColumn,
      index
    });
  }

  function getTableCell(column, row, index, tableColumnIndex) {
    const { key, type } = column
    //refactor this ASAP
    const cellValue = key || (type === 'confirm') ? renderCellBasedOnType(row[key], row, index, column) : renderActionColumn(row)
    return <Table.Cell key={`${key} ${row.id} ${index} ${tableColumnIndex}`}>{cellValue}</Table.Cell>
  }

  function tableBody() {
    return <Table.Body>
      {rows?.map((row, index) => {
        const displayPlaceholder = props.disableRowId && props.disableRowId === row?.id
        return displayPlaceholder ? renderPlaceholder(row) : <StyledTableRow key={row.id ?? row[props.idKey] + index}>
          {tableColumnHeaders.map((tableColumn, tableColumnIndex) => {
            return getTableCell(tableColumn, row, index, tableColumnIndex)
          })}
        </StyledTableRow>
      })}
    </Table.Body>
  }

  function renderInfoHeader(info) {
    return <InfoPopup
      content={info}
      styleIcon={{ cursor: 'pointer', fontSize: '1.2em', color: 'white' }}
    />
  }

  function renderHeaderTitle(name) {
    return <div style={{ marginRight: '5px' }}>{name}</div>
  }

  function renderSortedIcon(sorted) {
    const direction = sorted === 'desc' ? 'down' : 'up'
    return <Icon name={`chevron ${direction}`} />
  }

  const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

  function tableHeaders() {
    return <Table.Row>
      {tableColumnHeaders.map((tableColumn) => {
        const { name, info, sorted } = tableColumn
        return <Table.HeaderCell
          className={sorted ? 'sortable' : ''}
          selectable={Boolean(sorted)}
          onClick={() => sorted && setDataFiltered({ ...dataFiltered, column: camelToSnakeCase(tableColumn.key), sort: sorted === 'asc' ? 'desc' : 'asc' })}
          style={{ cursor: sorted ? 'pointer' : 'default' }}
          key={name + Math.random()}
        >
          <div style={{ display: 'flex', justifyContent: props.infoNextToText ? 'unset' : 'space-between', alignItems: 'baseline' }}>
            {renderHeaderTitle(name)}
            {sorted && renderSortedIcon(sorted)}
            {info && renderInfoHeader(info)}
          </div>
        </Table.HeaderCell>
      })}
    </Table.Row>
  }

  function renderNoResultsMessage() {
    return <StyledNoResultsMessage>
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          {t('No results found....')}
        </Header>
        <Segment.Inline>
        </Segment.Inline>
      </Segment>
    </StyledNoResultsMessage>
  }

  function renderPlaceholder(row) {
    return <Table.Row key={row?.id}>
      {tableColumnHeaders.map(tableColumnHeader => {
        return <Table.Cell key={tableColumnHeader.name}>
          <Placeholder>
            <Placeholder.Line />
          </Placeholder>
        </Table.Cell>
      })}
    </Table.Row>
  }

  function renderAnimatedTable() {
    return <Transition visible={!props.isFetchingData} animation='scale' duration={150}>
      <Table celled padded striped>
        <Table.Header>
          {tableHeaders()}
        </Table.Header>
        {rows && !props.isFetchingData && tableBody()}
        {props.loading && renderLoading()}
      </Table>
    </Transition>
  }

  function renderContentTable() {
    return <Table celled padded striped>
      <Table.Header>
        {tableHeaders()}
      </Table.Header>
      {rows && tableBody()}
    </Table>
  }

  function renderTable() {
    return <>
      {props.animated ? renderAnimatedTable() : renderContentTable()}
      {rows?.length === 0 && !props.isFetchingData && renderNoResultsMessage()}
    </>
  }

  function renderTableFooter() {
    return props.customTableFooter || <TableFooter
      dataFiltered={dataFiltered}
      total={total}
      onChangeData={(data) => setDataFiltered(data)}
      removePagination={props.removePagination}
    />
  }

  function renderLoading() {
    return <Dimmer inverted active={props.loading}>
      <Loader active={props.loading} />
    </Dimmer>
  }

  return <StyledTableWrapper color={firstColor}>
    {renderTable()}
    {!props.isFetchingData && rows.length > 0 && renderTableFooter()}
  </StyledTableWrapper>
}
export default TableComponent
